/**
 * Scans.tsx — Scan management page.
 *
 * Allows users to:
 *   - View all scans with status and provider badges
 *   - Trigger new scans via a form dialog
 *   - Expand a scan to see its findings (via ScanResults component)
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';

import ScanResults, { Finding } from '../components/ScanResults';
import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer { id: string; name: string; }

interface Scan {
  id: string;
  customer_id: string;
  provider: string;
  status: string;
  target: string;
  started_at: string | null;
  completed_at: string | null;
  findings: Finding[];
}

interface CustomerScanGroup {
  customer_id: string;
  customer_name: string;
  total_scans: number;
  latest_scan_date: string | null;
  scans: Scan[];
}

// ── Status chip ───────────────────────────────────────────────────────────────

type ChipColor = 'success' | 'info' | 'warning' | 'error' | 'default';

function statusChip(status: string): { color: ChipColor; label: string } {
  switch (status) {
    case 'completed':  return { color: 'success', label: 'Completed' };
    case 'running':    return { color: 'info',    label: 'Running' };
    case 'pending':    return { color: 'warning', label: 'Pending' };
    case 'failed':     return { color: 'error',   label: 'Failed' };
    case 'cancelled':  return { color: 'default', label: 'Cancelled' };
    default:           return { color: 'default', label: status };
  }
}

// ── Scan row (expandable) ─────────────────────────────────────────────────────

function ScanRow({ scan: initialScan, customers, hideCustomerCol = false }: {
  scan: Scan;
  customers: Customer[];
  hideCustomerCol?: boolean;
}) {
  const [scan, setScan] = useState<Scan>(initialScan);
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const { color, label } = statusChip(scan.status);
  const customerName = customers.find((c) => c.id === scan.customer_id)?.name ?? scan.customer_id;
  const inProgress = scan.status === 'running' || scan.status === 'pending';
  const colSpan = hideCustomerCol ? 6 : 7;

  // Poll every 5 s while the scan is running or pending; stop once it settles.
  useEffect(() => {
    if (!inProgress) return;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get<Scan>(`${API_BASE}/v1/scans/${scan.id}`);
        setScan(data);
        if (data.status !== 'running' && data.status !== 'pending') {
          clearInterval(id);
        }
      } catch { /* silently ignore transient errors */ }
    }, 5000);
    return () => clearInterval(id);
  }, [inProgress, scan.id]);

  async function handleCancel() {
    if (!window.confirm('Are you sure you want to stop this scan?')) return;
    setCancelling(true);
    try {
      const { data } = await axios.patch<Scan>(`${API_BASE}/v1/scans/${scan.id}/cancel`);
      setScan(data);
    } catch { /* ignore — polling will reflect the real status */ }
    finally { setCancelling(false); }
  }

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {!hideCustomerCol && (
          <TableCell><Typography variant="body2" fontWeight={500}>{customerName}</Typography></TableCell>
        )}
        <TableCell><Chip label={scan.provider.toUpperCase()} size="small" variant="outlined" /></TableCell>
        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{scan.target}</Typography></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={label} color={color} size="small" />
            {inProgress && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={cancelling}
                onClick={handleCancel}
                sx={{ py: 0, minWidth: 0, fontSize: '0.7rem', lineHeight: 1.4 }}
              >
                {cancelling ? <CircularProgress size={12} color="error" /> : 'Stop'}
              </Button>
            )}
          </Box>
        </TableCell>
        <TableCell><Typography variant="caption" color="text.secondary">{scan.findings.length}</Typography></TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {scan.started_at ? new Date(scan.started_at).toLocaleString() : '—'}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <ScanResults findings={scan.findings} scanStatus={scan.status} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Customer group card (admin view) ──────────────────────────────────────────

function CustomerGroupCard({ group, customers, defaultOpen }: {
  group: CustomerScanGroup;
  customers: Customer[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, cursor: 'pointer' }}
      >
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>{group.customer_name}</Typography>
        <Chip
          label={`${group.total_scans} scan${group.total_scans !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
        />
        {group.latest_scan_date && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Last: {new Date(group.latest_scan_date).toLocaleString()}
          </Typography>
        )}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <TableContainer sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'background.default' } }}>
                <TableCell padding="checkbox" />
                <TableCell>Provider</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Findings</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.scans.map((scan) => (
                <ScanRow key={scan.id} scan={scan} customers={customers} hideCustomerCol />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
}

// ── New scan dialog ───────────────────────────────────────────────────────────

interface NewScanDialogProps {
  open: boolean;
  customers: Customer[];
  onClose: () => void;
  onCreated: () => void;
}

function NewScanDialog({ open, customers, onClose, onCreated }: NewScanDialogProps) {
  const { user, loading: authLoading } = useAuth();
  const isCustomer = !authLoading && user?.role === 'customer';

  const [customerId, setCustomerId] = useState('');
  const [provider, setProvider] = useState('aws');
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For customer role: pin to their own org as soon as auth resolves.
  useEffect(() => {
    if (!authLoading && isCustomer) setCustomerId(user?.customer_id ?? '');
  }, [authLoading, isCustomer, user?.customer_id]);

  const handleSubmit = async () => {
    if (!customerId || !target) { setError('Customer and target are required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/v1/scans/`, { customer_id: customerId, provider, target });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trigger New Scan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {/* Partner selector — removed from DOM for customer role */}
        {!isCustomer && (
          <FormControl fullWidth>
            <InputLabel>Partner</InputLabel>
            <Select value={customerId} label="Partner" onChange={(e) => setCustomerId(e.target.value)}>
              {customers.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth>
          <InputLabel>Provider</InputLabel>
          <Select value={provider} label="Provider" onChange={(e) => setProvider(e.target.value)}>
            {['aws', 'gcp', 'azure', 'ssh'].map((p) => (
              <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Target (Account ID / Project / Hostname)"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          fullWidth
          placeholder={provider === 'ssh' ? '192.168.1.10' : provider === 'aws' ? '123456789012' : 'my-project-id'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : 'Start Scan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Scans() {
  const { user, loading: authLoading, isEngineer } = useAuth();
  const isCustomer = !authLoading && user?.role === 'customer';
  const isAdminView = !authLoading && isEngineer;

  const [scans, setScans] = useState<Scan[]>([]);
  const [scanGroups, setScanGroups] = useState<CustomerScanGroup[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const customersPromise = !isCustomer
        ? axios.get<Customer[]>(`${API_BASE}/v1/customers/`).catch(() => null)
        : Promise.resolve(null);

      if (isAdminView) {
        const [groupsRes, customersRes] = await Promise.all([
          axios.get<CustomerScanGroup[]>(`${API_BASE}/v1/scans/grouped`),
          customersPromise,
        ]);
        setScanGroups(groupsRes.data);
        if (customersRes) setCustomers(customersRes.data);
      } else {
        const [scansRes, customersRes] = await Promise.all([
          axios.get<Scan[]>(`${API_BASE}/v1/scans/`),
          customersPromise,
        ]);
        setScans(scansRes.data);
        if (customersRes) setCustomers(customersRes.data);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.message ?? 'Failed to load scans'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!authLoading) load(); }, [authLoading]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Scans</Typography>
        <Box display="flex" gap={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">
            Refresh
          </Button>
          <Button startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} variant="contained" size="small">
            New Scan
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : isAdminView ? (
        // ── Admin grouped view ──────────────────────────────────────────────
        <>
          {scanGroups.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No scans found across any customers.</Typography>
            </Paper>
          ) : (
            scanGroups.map((group, idx) => (
              <CustomerGroupCard
                key={group.customer_id}
                group={group}
                customers={customers}
                defaultOpen={idx === 0}
              />
            ))
          )}
        </>
      ) : (
        // ── Customer / partner flat view ────────────────────────────────────
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'background.default' } }}>
                <TableCell padding="checkbox" />
                <TableCell>Partner</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Findings</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.map((scan) => (
                <ScanRow key={scan.id} scan={scan} customers={customers} />
              ))}
              {scans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No scans found. Trigger one to get started.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <NewScanDialog
        open={dialogOpen}
        customers={customers}
        onClose={() => setDialogOpen(false)}
        onCreated={load}
      />
    </Box>
  );
}
